import { Grid } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { QuoteGetResponse } from '@lio/api-types';
import { useSnackbar } from 'notistack';
import { Dialog } from '@components/dialog';
import { Checkbox, FormControlLabel } from '@components/index';
import { TextField } from '@components/textField/TextField';
import { useInsertNote } from '@services/note';
import { ToastType } from '@components/toast/ToastType.enum';
import { NoteType } from '@models/note';
import { useCreateQuoteStatusChange } from '@services/quotes';
import { QuoteStatusId } from '@models/quotes';

interface IAcceptQuoteDialog {
  isOpen: boolean;
  closeDialog: () => void;
  quote: QuoteGetResponse;
}

const initialState = {
  notes: '',
  message: '',
  confirmation: false,
};

export const AcceptQuoteDialog = ({ isOpen, closeDialog, quote }: IAcceptQuoteDialog) => {
  const { createQuoteStatusChange, isPending: isCreateQuoteStatusChangeLoading } = useCreateQuoteStatusChange();
  const { insertNoteAsync } = useInsertNote();
  const { enqueueSnackbar } = useSnackbar();
  const [acceptance, setAcceptance] = useState(initialState);
  const { notes, message, confirmation } = acceptance;

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setAcceptance((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleCheckboxChange = () => {
    setAcceptance((prev) => ({
      ...prev,
      confirmation: !prev.confirmation,
    }));
  };

  const handleClose = () => {
    setAcceptance(initialState);
    closeDialog();
  };

  const handleAcceptQuote = async () => {
    const acceptQuoteBody = {
      quoteId: quote.id,
      statusId: QuoteStatusId.Rated,
      quoteStatusChangeReason: {
        reason: '',
      },
    };
    try {
      createQuoteStatusChange(acceptQuoteBody, {
        onError: () => {
          enqueueSnackbar(
            `Release of quote #${quote.policyNumber?.policyNumber} has failed. This quote status is not valid to release to the broker.`,
            {
              preventDuplicate: true,
              variant: ToastType.Error,
              persist: true,
            },
          );
        },
      }).then(() => {
        if (notes !== '') {
          const insertNoteBody = {
            type: NoteType.Note,
            body: notes,
            quoteId: quote.id,
            customerId: quote.customer.id,
            brokerageId: quote.brokerage.id,
            rawText: notes,
          };
          insertNoteAsync({ noteRequestBody: insertNoteBody });
        }
        if (message !== '') {
          const insertMessageBody = {
            type: NoteType.Message,
            body: message,
            rawText: message,
            quoteId: quote.id,
            customerId: quote.customer.id,
            brokerageId: quote.brokerage.id,
          };
          insertNoteAsync({ noteRequestBody: insertMessageBody });
        }
        setAcceptance(initialState);
        handleClose();
        enqueueSnackbar(`Quote #${quote.policyNumber?.policyNumber} was successfully released.`, {
          preventDuplicate: true,
          variant: ToastType.Success,
        });
      });
    } catch {
      enqueueSnackbar(`Release of quote #${quote.policyNumber?.policyNumber} has failed.`, {
        preventDuplicate: true,
        variant: ToastType.Error,
        persist: true,
      });
    }
  };

  return (
    <Dialog
      open={isOpen}
      title={`Acceptance of Quote #${quote.policyNumber?.policyNumber}`}
      handleCloseDialog={handleClose}
      primaryBtnText='Release Quote'
      primaryBtnAction={handleAcceptQuote}
      isPrimaryBtnLoading={isCreateQuoteStatusChangeLoading}
      isPrimaryBtnDisabled={!confirmation}
      secondaryBtnText='Exit'
      secondaryBtnAction={handleClose}
    >
      <Grid
        container
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          pb: 2,
        }}
      >
        <Grid
          size={{ xs: 11 }}
          sx={{
            marginTop: '16px',
            '.MuiFormControl-root': {
              width: '100%',
              '& .MuiFormLabel-root': {
                marginLeft: '16px',
              },
            },
          }}
        >
          <TextField
            inputLabel='Notes (Optional)'
            name='notes'
            value={notes}
            onChange={handleChange}
            sx={{ marginBottom: '24px' }}
            multiline
            rows={3}
            helperText='This is internal only and will be saved in the “Notes” sidebar for this quote'
          />
          {!quote.hasInternalOnlyAccessRestriction && (
            <TextField
              inputLabel='Message to Broker (Optional)'
              helperText='Adding an optional message will send an email to the broker'
              name='message'
              value={message}
              onChange={handleChange}
              sx={{ marginBottom: '24px' }}
              multiline
              rows={3}
            />
          )}
          <FormControlLabel
            label="I'm sure that I want to release this quote to the broker"
            typographyProps={{ variant: 'bodySmall', fontWeight: 'bold' }}
            name='confirmation'
            control={<Checkbox checked={confirmation} onChange={handleCheckboxChange} />}
          />
        </Grid>
      </Grid>
    </Dialog>
  );
};
